import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { useAuth0 } from '@auth0/auth0-react';

const ChatUIPersistenceContext = createContext(null);

/** sessionStorage: survives modal close and client-side navigation; cleared when the tab closes. */
const STORAGE_PREFIX = 'canopy_chat_ui_v1:';

/** Avoid storage quota issues if threads grow very long (UI only; agent history is server-managed). */
const MAX_PERSISTED_UI_MESSAGES = 120;

function trimMessages(messages) {
    if (!Array.isArray(messages) || messages.length <= MAX_PERSISTED_UI_MESSAGES) {
        return messages ?? [];
    }
    return messages.slice(-MAX_PERSISTED_UI_MESSAGES);
}

function readStored(key) {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) {
            return {
                messages: [],
                agentSessionId: null,
                serverHasConversationRows: false,
                isChatModalOpen: false,
            };
        }
        const data = JSON.parse(raw);
        return {
            messages: trimMessages(Array.isArray(data.messages) ? data.messages : []),
            agentSessionId: data.agentSessionId ?? null,
            serverHasConversationRows: !!data.serverHasConversationRows,
            isChatModalOpen: !!data.isChatModalOpen,
        };
    } catch {
        return {
            messages: [],
            agentSessionId: null,
            serverHasConversationRows: false,
            isChatModalOpen: false,
        };
    }
}

export function ChatUIPersistenceProvider({ children }) {
    const { user, isAuthenticated, isLoading } = useAuth0();

    const userKey = useMemo(() => {
        if (isAuthenticated && user) {
            return user.nickname || user.email || user.sub || 'user';
        }
        try {
            const stored = localStorage.getItem('userVal');
            if (stored) {
                const parsed = JSON.parse(stored);
                return parsed?.data ?? 'anon';
            }
        } catch {
            /* ignore */
        }
        return 'anon';
    }, [isAuthenticated, user]);

    const storageKey = `${STORAGE_PREFIX}${encodeURIComponent(String(userKey))}`;

    const [messages, setMessagesState] = useState([]);
    /** Agent API session: keep stable for the thread until Clear chat — backend keys pending MQTT commands by username + sessionId. */
    const [agentSessionId, setAgentSessionIdState] = useState(null);
    const [serverHasConversationRows, setServerHasConversationRowsState] = useState(false);
    const [isChatModalOpen, setIsChatModalOpenState] = useState(false);
    const hydratedRef = useRef(false);
    /** Avoid writing the previous user's transcript into a new storage key on user switch. */
    const skipNextPersistRef = useRef(false);

    // Load from sessionStorage when auth resolved or user key changes
    useEffect(() => {
        if (isLoading) return;
        skipNextPersistRef.current = true;
        const loaded = readStored(storageKey);
        setMessagesState(loaded.messages);
        setAgentSessionIdState(loaded.agentSessionId);
        setServerHasConversationRowsState(loaded.serverHasConversationRows);
        setIsChatModalOpenState(loaded.isChatModalOpen);
        hydratedRef.current = true;
    }, [isLoading, storageKey]);

    // Persist after hydration
    useEffect(() => {
        if (isLoading || !hydratedRef.current) return;
        if (skipNextPersistRef.current) {
            skipNextPersistRef.current = false;
            return;
        }
        try {
            sessionStorage.setItem(
                storageKey,
                JSON.stringify({
                    messages: trimMessages(messages),
                    agentSessionId,
                    serverHasConversationRows,
                    isChatModalOpen,
                }),
            );
        } catch (e) {
            console.warn('[ChatUIPersistence] sessionStorage write failed:', e);
        }
    }, [messages, agentSessionId, serverHasConversationRows, isChatModalOpen, storageKey, isLoading]);

    const setMessages = useCallback((update) => {
        setMessagesState((prev) => {
            const next = typeof update === 'function' ? update(prev) : update;
            return trimMessages(Array.isArray(next) ? next : prev);
        });
    }, []);

    const setAgentSessionId = useCallback((update) => {
        setAgentSessionIdState((prev) => (typeof update === 'function' ? update(prev) : update));
    }, []);

    const setServerHasConversationRows = useCallback((update) => {
        setServerHasConversationRowsState((prev) =>
            typeof update === 'function' ? update(prev) : update,
        );
    }, []);

    const setIsChatModalOpen = useCallback((update) => {
        setIsChatModalOpenState((prev) =>
            typeof update === 'function' ? update(prev) : update,
        );
    }, []);

    const clearPersistedChat = useCallback(() => {
        setMessagesState([]);
        setAgentSessionIdState(null);
        setServerHasConversationRowsState(false);
        setIsChatModalOpenState(false);
        try {
            sessionStorage.removeItem(storageKey);
        } catch {
            /* ignore */
        }
    }, [storageKey]);

    const value = useMemo(
        () => ({
            messages,
            setMessages,
            agentSessionId,
            setAgentSessionId,
            serverHasConversationRows,
            setServerHasConversationRows,
            isChatModalOpen,
            setIsChatModalOpen,
            clearPersistedChat,
        }),
        [
            messages,
            setMessages,
            agentSessionId,
            setAgentSessionId,
            serverHasConversationRows,
            setServerHasConversationRows,
            isChatModalOpen,
            setIsChatModalOpen,
            clearPersistedChat,
        ],
    );

    return (
        <ChatUIPersistenceContext.Provider value={value}>
            {children}
        </ChatUIPersistenceContext.Provider>
    );
}

ChatUIPersistenceProvider.propTypes = {
    children: PropTypes.node,
};

export function useChatUIPersistence() {
    const ctx = useContext(ChatUIPersistenceContext);
    if (!ctx) {
        throw new Error('useChatUIPersistence must be used within ChatUIPersistenceProvider');
    }
    return ctx;
}
